import { z } from "zod";
import { DEFAULT_PRODUCT, Product } from "./product.js";

// ---------------------------------------------------------------------------
// 料金プランと無料トライアル（縦串11、034、docs/26。射場さん 2026-09-20 の決定）
//   - 料金は3プラン・月額・会社単位（2026-09-15 トップページ公開の料金表と同じ）。金額は税抜の整数（円）
//   - 税込は API が tax_rate_rules（制度マスタ）の標準税率で計算して返す。ここに税率を書かない
//   - 無料トライアルは 30日・カード登録なし。終わってカードが未登録なら「見るだけ」
//     （ログイン・閲覧・PDF・データの持ち出しはできる。新しく作る・直す・出すはできない。データは消えない）
//   - カードでのお支払い（PAY.JP）の受付が始まる前（.env の BILLING_LIVE_FROM が空）は、だれも制限しない
//   - トップページ（deploy/caddy/www/index.html）の料金表は静的な HTML。金額を変えるときは両方を直す
//   - 料金表は製品ごと（049、E0-a、../eigyouryoku/docs/06 §2.2）：PLAN_CATALOG[product]。営業力の3プランは人数などは現場力と同じで、月額だけ違う（2026-10-14 に現場力だけ改めた）。
//     オプションは 在庫（営業力だけ。数える品目の数で3段）と 帳票デザイン（両方）と 工程管理（#100。#142 段5 から両方）と
//     書類の読み取り（両方。受け取った書類の2回目、../eigyouryoku/docs/13 ★7＝980円税抜。改定するときはここの1か所）。今までの PLAN_CATALOG[plan] は planSpec(plan) に
// ---------------------------------------------------------------------------

/** 料金プラン（tenants.plan。パーソナル／スモール／ビジネス） */
export const Plan = z.enum(["personal", "small", "business"]);
export type Plan = z.infer<typeof Plan>;

export type PlanSpec = {
  label: string;
  /** 月額（税抜、円） */
  priceExTax: number;
  /** 使える人数 */
  seats: number;
  /** 見積の雛形の上限（023） */
  templates: number;
  /** 見積の履歴と差分（036、docs/27）：出した見積を「直して出し直す」と前の回が残り、回どうしをくらべられる */
  revisionHistory: boolean;
  /**
   * 読み取りの月の通数の上限（047 の{vendorQuote}の取り込み＋受け取った書類の読み取りの合算。../eigyouryoku/docs/13 決めごと12・★3）。
   * 受付開始前（BILLING_LIVE_FROM が空）はだれも制限しない
   */
  readsPerMonth: number;
  /** 保存できる容量（GB。受け取った書類・写真・帳票の PDF などファイルの和。../eigyouryoku/docs/13 ★2） */
  storageGb: number;
  /**
   * パートナー手数料（#213、docs/40 §9。モジュール referral）。1回目は全プランで使える。
   * プラン別の ON/OFF は運営の管理画面（#66）で plan_features に移す＝コードは planAllows(plan, "referralFee") で聞くだけ
   */
  referralFee: boolean;
  tagline: string;
};

/** 在庫オプションの段（数える品目の数。月額・税抜。境と上の段の額は ../eigyouryoku/docs/06 ★2） */
export type StockTier = { key: "300" | "2000" | "10000"; items: number; price: number };

export type PlanOptions = {
  /** 在庫（モジュール stock。営業力だけ。ON/OFF の画面は E6） */
  stock?: { tiers: readonly StockTier[] };
  /** 帳票デザイン（モジュール paper_design。自分の書式。月額・税抜） */
  paperDesign: { price: number };
  /** 工程管理（モジュール schedule、#100、docs/39。月額・税抜）。#142 段5 から営業力にも（名前は製品ごと＝PRODUCTS の words.scheduleOption：工程管理／スケジュール管理） */
  schedule?: { price: number };
  /** 書類の読み取り（モジュール doc_read、受け取った書類の2回目。月額・税抜。両製品。通数はプランの readsPerMonth の中） */
  docRead: { price: number };
};

export type ProductPlanCatalog = {
  plans: Record<Plan, PlanSpec>;
  options: PlanOptions;
};

/**
 * 書類の読み取りの月額（税抜。2026-10-11 射場さんの決定 ★7＝980円。改定するかもしれない＝ここの1か所を直す。
 * 紹介ページの静的な料金表（deploy/caddy/www・www-eigyo）と特定商取引法の表記は同じ数字に保つ＝product.test が見比べる）
 */
export const DOC_READ_PRICE = 980;

/** 読み取りの月の通数（プランごと。{vendorQuote}と受け取った書類の合算） */
export const READS_PER_MONTH = { personal: 10, small: 50, business: 200 } as const;

/** 現場力の3プラン。並びは安い順。2026-10-14 射場さんの決定で月額を改めた（旧 900／1,980／3,980円 → 1,400／2,900／4,900円、税抜。営業力は変えない） */
const GENBA_PLANS: Record<Plan, PlanSpec> = {
  personal: { label: "パーソナル", priceExTax: 1400, seats: 1, templates: 1, revisionHistory: false, readsPerMonth: READS_PER_MONTH.personal, storageGb: 1, referralFee: true, tagline: "個人事業主・一人親方に" },
  small: { label: "スモール", priceExTax: 2900, seats: 3, templates: 3, revisionHistory: true, readsPerMonth: READS_PER_MONTH.small, storageGb: 5, referralFee: true, tagline: "少人数のチームに" },
  business: { label: "ビジネス", priceExTax: 4900, seats: 20, templates: 10, revisionHistory: true, readsPerMonth: READS_PER_MONTH.business, storageGb: 10, referralFee: true, tagline: "会社として使うなら" },
};

/** 営業力の3プラン。人数・雛形などは現場力と同じ（E0-a。文だけ売る会社向けに）。月額は営業力の数字（2026-10-14 に現場力だけ改めたので分けた） */
const EIGYO_PLANS: Record<Plan, PlanSpec> = {
  personal: { ...GENBA_PLANS.personal, priceExTax: 900, tagline: "個人事業主・フリーランスに" },
  small: { ...GENBA_PLANS.small, priceExTax: 1980, tagline: "少人数のチームに" },
  business: { ...GENBA_PLANS.business, priceExTax: 3980, tagline: "会社として使うなら" },
};

/** 料金表（製品ごと）。PLAN_CATALOG.genba.plans.personal のように引く。プランの数字だけなら planSpec(plan, product) */
export const PLAN_CATALOG: Record<Product, ProductPlanCatalog> = {
  genba: { plans: GENBA_PLANS, options: { paperDesign: { price: 500 }, schedule: { price: 2980 }, docRead: { price: DOC_READ_PRICE } } },
  eigyo: {
    plans: EIGYO_PLANS,
    options: {
      stock: {
        tiers: [
          { key: "300", items: 300, price: 1980 },
          { key: "2000", items: 2000, price: 3980 },
          { key: "10000", items: 10000, price: 6980 },
        ],
      },
      paperDesign: { price: 500 },
      // スケジュール管理（#142 段5、2026-10-06 射場さん）：現場力の工程管理と同じもの・同じ額
      schedule: { price: 2980 },
      docRead: { price: DOC_READ_PRICE },
    },
  },
};
export const PLAN_ORDER: readonly Plan[] = ["personal", "small", "business"];

/** 保存できる容量（バイト）。プランの storageGb × 1024³ */
export const storageLimitBytes = (spec: PlanSpec): number => spec.storageGb * 1024 * 1024 * 1024;

/** プランの数字（人数・雛形・履歴）。製品を省けば現場力（今までの PLAN_CATALOG[plan] と同じ答え） */
export function planSpec(plan: Plan, product: Product = DEFAULT_PRODUCT): PlanSpec {
  return PLAN_CATALOG[product].plans[plan];
}
/** DB から来た文字列のプラン用。知らない値は undefined */
export function planSpecOf(plan: string, product: Product = DEFAULT_PRODUCT): PlanSpec | undefined {
  const p = Plan.safeParse(plan);
  return p.success ? planSpec(p.data, product) : undefined;
}

/** プランで開け閉めする機能（#213。運営の管理画面 #66 で plan_features に移すまでは PlanSpec の真偽） */
export type PlanFeature = "referralFee";
/** そのプランで使える機能か。知らないプランは false（受付開始の前・トライアル中の扱いは呼ぶ側＝今までどおり全機能） */
export function planAllows(plan: string, feature: PlanFeature, product: Product = DEFAULT_PRODUCT): boolean {
  const spec = planSpecOf(plan, product);
  return spec ? spec[feature] === true : false;
}

/** 無料トライアルの日数（トップページの「30日間の無料トライアル」と同じ） */
export const TRIAL_DAYS = 30;

/**
 * 契約の段階。
 *   prelaunch … カードでのお支払いの受付が始まる前。全機能・無料
 *   trial     … 無料トライアル中。全機能
 *   expired   … トライアルが終わり、お支払いの登録が無い。見るだけ
 * （お支払いの登録ができたあとの active などは、PAY.JP をつなぐ回で足す）
 */
export const BillingPhase = z.enum(["prelaunch", "trial", "expired"]);
export type BillingPhase = z.infer<typeof BillingPhase>;

export const BillingStatus = z.object({
  phase: BillingPhase,
  plan: Plan,
  /** カードでのお支払いの受付が始まる（始まった）日時（ISO）。決まっていなければ null */
  liveFrom: z.string().nullable(),
  /** 無料トライアルの終わり（ISO）。受付の開始が決まっていなければ null */
  trialEndsAt: z.string().nullable(),
  /** トライアルの残り日数（日本時間の暦で、きょうから最後の日まで。最後の日は 0＝「きょうまで」。trial のときだけ） */
  daysLeft: z.number().int().nullable(),
  /** 見るだけ（新しく作る・直す・出すができない） */
  readOnly: z.boolean(),
});
export type BillingStatus = z.infer<typeof BillingStatus>;

const DAY = 24 * 60 * 60 * 1000;
const JST = 9 * 60 * 60 * 1000;

/** その日時を含む日本時間の日の終わり（翌日 0:00 JST）。「10月20日まで」と言った日の夜中まで使える */
function endOfJstDay(d: Date): Date {
  const j = d.getTime() + JST;
  return new Date(Math.floor(j / DAY) * DAY + DAY - JST);
}

/** 日本時間の暦の日の通し番号（日数の引き算に使う） */
function jstDayNo(d: Date): number {
  return Math.floor((d.getTime() + JST) / DAY);
}

/**
 * いまの契約の段階を決める（DB も時計も読まない純粋な関数。API とテストが同じものを使う）。
 *   トライアルの終わり ＝ 上書き（tenants.trial_ends_at）があればそれ。無ければ
 *   「会社を作った日」と「受付開始日」の遅いほう ＋ 30日 の、日本時間でその日の終わり
 *   （受付開始の前から使っている会社は、受付開始の日から 30日）
 */
export function billingStatusOf(input: { now: Date; liveFrom: Date | null; tenantCreatedAt: Date; trialEndsAtOverride: Date | null; plan: Plan }): BillingStatus {
  const { now, liveFrom, tenantCreatedAt, trialEndsAtOverride, plan } = input;
  if (!liveFrom) return { phase: "prelaunch", plan, liveFrom: null, trialEndsAt: null, daysLeft: null, readOnly: false };
  const start = tenantCreatedAt.getTime() > liveFrom.getTime() ? tenantCreatedAt : liveFrom;
  const computed = endOfJstDay(new Date(start.getTime() + TRIAL_DAYS * DAY));
  const end = trialEndsAtOverride ?? computed;
  const base = { plan, liveFrom: liveFrom.toISOString(), trialEndsAt: end.toISOString() };
  if (now.getTime() < liveFrom.getTime()) return { phase: "prelaunch", ...base, daysLeft: null, readOnly: false };
  if (now.getTime() < end.getTime()) return { phase: "trial", ...base, daysLeft: jstDayNo(new Date(end.getTime() - 1)) - jstDayNo(now), readOnly: false };
  return { phase: "expired", ...base, daysLeft: null, readOnly: true };
}

/** 税込（円）。当社の売上の消費税は 1円未満切り捨て。税率は API が制度マスタから引いて渡す */
export function priceInTax(priceExTax: number, taxRate: number): number {
  return priceExTax + Math.floor(priceExTax * taxRate + 1e-9);
}

export const BILLING_MSG = {
  readOnly: "無料トライアルが終了したため、閲覧のみの状態です（作成・修正・発行はできません）。続けるには、パソコンの料金プランからお支払い方法を登録してください",
  adminOnly: "料金プランの変更は管理者のみ行えます",
  planInvalid: "プランを選んでください",
  planChanged: (label: string) => `料金プランを「${label}」に変更しました`,
  /** 下げられない理由（#287：言葉の決まりで見直し。docs/100） */
  templatesOver: (n: number, limit: number, label: string) => `テンプレートが${n}件のため変更できません（${label}は${limit}件まで）。設定 → 見積書のテンプレート で${n - limit}件削除してください`,
  seatsOver: (n: number, limit: number, label: string) => `利用人数が${n}人のため変更できません（${label}は${limit}人まで）`,
  /** 在庫オプションの1行（料金プランの画面・特商法の表記。ボタンは E6 で足す） */
  stockOption: (tiers: readonly { items: number; priceExTax: number }[]) => {
    const [first, ...rest] = tiers;
    if (!first) return "";
    const yen = (n: number) => n.toLocaleString("ja-JP");
    const tail = rest.map((t) => `${yen(t.items)} まで ${yen(t.priceExTax)}円`).join("、");
    return `在庫オプション ${yen(first.priceExTax)}円〜（数える品目 ${yen(first.items)} まで${tail ? `。${tail}` : ""}）`;
  },
} as const;

/**
 * 料金プランの画面の文（#287、2026-11-01 射場さん ★5、docs/100・Figma「文言と画面の見直し（11/1）」⑤）。
 * 形：見出しの行（戻る・題・？・右に「パソコン専用」）→ 現在のプラン（プラン名・状態チップ・月額／利用状況の4枚／注と「お支払い方法登録」）→
 *     プラン変更（3枚。選択中は藍の帯）→ オプション（表。状態チップ・申込／解約は #69 まで押せない）→ 下の1行
 */
export const PLAN_PAGE_MSG = {
  title: "料金プラン",
  titleHelp: "料金は会社ごとに発生します。プラン変更は管理者のみ行えます。",
  pcOnly: "パソコン専用",
  current: "現在のプラン",
  monthly: (exTax: string, inTax: string, seats: number) => `月額 ${exTax}円（税込 ${inTax}円）・${seats}人まで`,
  usageSeats: "利用人数",
  usageTemplates: "テンプレート",
  usageStorage: "保存容量",
  usageReads: "AI読み取り（今月）",
  /** 上限の無いとき（受付開始前の読み取りの通数） */
  noLimit: "—",
  chipPrelaunch: "無料期間",
  chipTrial: (daysLeft: number) => (daysLeft === 0 ? "無料トライアル 本日まで" : `無料トライアル 残り ${daysLeft}日`),
  chipExpired: "閲覧のみ",
  notePrelaunch: "お支払いの受付開始まで料金はかかりません。開始日はメールで案内します。",
  notePrelaunchDated: (date: string, trialDays: number) => `お支払いの受付開始は ${date} です。開始日から${trialDays}日間の無料トライアル中にお支払い方法を登録すると、継続して利用できます。`,
  noteTrial: (date: string) => `無料トライアル期間（${date}まで）は料金がかかりません。終了後もデータは残ります。`,
  /** {export} は「データの持ち出し」のリンク（設定 → 退会） */
  noteExpired: (date: string) => `無料トライアルは${date}に終了しました。作成・修正・発行にはお支払い方法の登録が必要です（閲覧・PDF・{export}は可）。`,
  exportLink: "データの持ち出し",
  payMethod: "お支払い方法登録",
  payMethodSoon: "お支払い方法の登録は受付開始後に行えます",
  change: "プラン変更",
  /** {vendorQuote} は画面が t() で埋める */
  changeHelp: "利用人数で選ぶ月額のプランです。下げるときは、利用人数とテンプレートの件数がそのプランの上限以内である必要があります。AI読み取りの通数は{vendorQuote}と受け取った書類の合計です。",
  changeNote: "月額・初期費用 0円・無料期間中は何度でも変更できます",
  rowSeats: "利用人数",
  rowTemplates: "見積書のテンプレート",
  rowStorage: "保存容量",
  rowReads: "AI読み取り（月）",
  rowHistory: "見積の履歴と差分",
  yes: "あり",
  no: "—",
  perMonth: "円／月（税抜）",
  inTax: (inTax: string) => `税込 ${inTax}円`,
  selected: "選択中",
  changeTo: (label: string) => `${label}に変更`,
  undo: "元に戻す",
  options: "オプション",
  optionsNote: "プランに追加する機能（月額・税抜）",
  optionPaper: "帳票デザイン（カスタム書式）",
  optionStock: (items: string) => `在庫（${items}品目まで）`,
  apply: "申込",
  cancel: "解約",
  optionsFoot: "申込・解約はお支払い方法の登録開始後に行えます。",
  /** {legal} は「特定商取引法に基づく表記」のリンク */
  foot: "21人以上は別途お見積りします。料金・支払・解約の規定は「{legal}」に記載しています。",
  legal: "特定商取引法に基づく表記",
} as const;

/** オプションの状態（#287）：on＝利用中／trial＝利用中（お試し）＝製品の既定・受付開始前で入っている／off＝未契約 */
export const OptionState = z.enum(["on", "trial", "off"]);
export type OptionState = z.infer<typeof OptionState>;
export const OPTION_STATE_LABEL: Record<OptionState, string> = { on: "利用中", trial: "利用中（お試し）", off: "未契約" };

/** 保存容量の GB の表示（小数1桁・切り上げ＝使っていれば 0 にしない）。例 0.1 */
export function storageGbText(bytes: number): string {
  if (bytes <= 0) return "0";
  const tenths = Math.ceil((bytes / (1024 * 1024 * 1024)) * 10);
  return (tenths / 10).toFixed(1);
}

/** 料金表の1行（API が税込を足して返す） */
export const PlanOffer = z.object({
  code: Plan,
  label: z.string(),
  tagline: z.string(),
  priceExTax: z.number().int(),
  priceInTax: z.number().int(),
  seats: z.number().int(),
  templates: z.number().int(),
  /** 見積の履歴と差分が使えるか（036） */
  revisionHistory: z.boolean(),
  /** 保存できる容量（GB。../eigyouryoku/docs/13 ★2） */
  storageGb: z.number().int(),
  /** 読み取りの月の通数（{vendorQuote}と受け取った書類の合算。../eigyouryoku/docs/13 決めごと12） */
  readsPerMonth: z.number().int(),
});
export type PlanOffer = z.infer<typeof PlanOffer>;

/** オプションの料金（API が税込を足して返す。049） */
export const StockTierOffer = z.object({
  key: z.enum(["300", "2000", "10000"]),
  items: z.number().int(),
  priceExTax: z.number().int(),
  priceInTax: z.number().int(),
});
export type StockTierOffer = z.infer<typeof StockTierOffer>;
export const PlanOptionsOffer = z.object({
  /** 在庫（営業力だけ。現場力は null） */
  stock: z.object({ tiers: z.array(StockTierOffer) }).nullable(),
  /** 帳票デザイン（自分の書式。両方の製品） */
  paperDesign: z.object({ priceExTax: z.number().int(), priceInTax: z.number().int() }),
  /** 工程管理（#100。#142 段5 から営業力も＝スケジュール管理。料金表に無い製品は null） */
  schedule: z.object({ priceExTax: z.number().int(), priceInTax: z.number().int() }).nullable(),
  /** 書類の読み取り（両製品。受け取った書類の2回目） */
  docRead: z.object({ priceExTax: z.number().int(), priceInTax: z.number().int() }),
});
export type PlanOptionsOffer = z.infer<typeof PlanOptionsOffer>;

/** だれでも見られる料金表（特定商取引法に基づく表記・料金プランの画面が使う）。製品は Host（ログイン中は会社の製品） */
export const PlanList = z.object({
  product: Product,
  plans: z.array(PlanOffer),
  options: PlanOptionsOffer,
  /** 標準税率（例 0.1）。制度マスタ tax_rate_rules から */
  taxRate: z.number(),
  trialDays: z.number().int(),
});
export type PlanList = z.infer<typeof PlanList>;

/** アカウント → 料金プラン */
export const PlanPage = z.object({
  product: Product,
  billing: BillingStatus,
  plans: z.array(
    PlanOffer.extend({
      current: z.boolean(),
      /** このプランに変えられない理由（雛形が多すぎる など）。変えられるなら null */
      blockedReason: z.string().nullable(),
    }),
  ),
  options: PlanOptionsOffer,
  taxRate: z.number(),
  trialDays: z.number().int(),
  /**
   * いま使っている数（プランを下げるときの目安と、現在のプランの利用状況の4枚。#287）。
   * storageBytes＝fn_tenant_storage_bytes（受け取った書類・写真・帳票の PDF などのファイルの和）、
   * reads＝今月の AI読み取りの通数（fn_ai_read_month_count）と上限（受付開始前は null＝「—」）
   */
  usage: z.object({
    seats: z.number().int(),
    templates: z.number().int(),
    storageBytes: z.number().int().default(0),
    readsUsed: z.number().int().default(0),
    readsLimit: z.number().int().nullable().default(null),
  }),
  /** オプションの状態（#287。料金表に無いオプションは null） */
  optionStates: z
    .object({
      stock: OptionState.nullable(),
      paperDesign: OptionState,
      schedule: OptionState.nullable(),
      docRead: OptionState,
    })
    .default({ stock: null, paperDesign: "off", schedule: null, docRead: "off" }),
  /** 料金プランを変えられる人か（管理者だけ） */
  canChange: z.boolean(),
});
export type PlanPage = z.infer<typeof PlanPage>;

export const PlanChangeRequest = z.object({ plan: Plan });
export type PlanChangeRequest = z.infer<typeof PlanChangeRequest>;
